'use client';

import { useState } from 'react';
import KioskNav from '../shared/KioskNav';

interface PaymentPageProps {
  palletQuantity: number;
  initialPayment: string;
  onPaymentChange: (method: string) => void;
  onPrevious: () => void;
  onNext: () => void;
}

export default function PaymentPage({
  palletQuantity,
  initialPayment,
  onPaymentChange,
  onPrevious,
  onNext,
}: PaymentPageProps) {
  const [selectedPayment, setSelectedPayment] = useState(initialPayment);

  const unitPrice = 5000;
  const totalAmount = palletQuantity * unitPrice;

  // props 변경 시 동기화 (렌더 중 조정 — effect 내 setState 회피)
  const [prevInitialPayment, setPrevInitialPayment] = useState(initialPayment);
  if (prevInitialPayment !== initialPayment) {
    setPrevInitialPayment(initialPayment);
    setSelectedPayment(initialPayment);
  }

  const handlePaymentSelect = (method: string) => {
    setSelectedPayment(method);
    onPaymentChange(method);
  };

  const handleNext = () => {
    if (selectedPayment) {
      onNext();
    }
  };

  return (
    <div className="h-full bg-gray-100 flex flex-col justify-between py-6 px-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-5xl font-bold text-gray-800">결제정보</h1>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center space-y-8">
        {/* Payment Calculation */}
        <p className="text-3xl text-red-500 font-semibold">
          파레트총수량 {palletQuantity} * 5,000 = {totalAmount.toLocaleString()}원
        </p>

        {/* Payment Method Selection */}
        <div className="flex justify-center items-center space-x-12">
          <button
            onClick={() => handlePaymentSelect('입금')}
            className={`px-12 py-6 rounded-2xl text-2xl font-semibold transition-all duration-300 ${
              selectedPayment === '입금'
                ? 'bg-yellow-500 text-white shadow-lg scale-105'
                : 'bg-yellow-300 text-white opacity-50'
            }`}
          >
            입금
          </button>

          <button
            onClick={() => handlePaymentSelect('카드')}
            className={`px-12 py-6 rounded-2xl text-2xl font-semibold transition-all duration-300 ${
              selectedPayment === '카드'
                ? 'bg-blue-600 text-white shadow-lg scale-105'
                : 'bg-blue-400 text-white opacity-50'
            }`}
          >
            카드
          </button>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col space-y-4">
        {/* Navigation Buttons */}
        <div className="flex justify-between px-8">
          {/* Previous Button */}
          <button
            onClick={onPrevious}
            className="bg-green-500 hover:bg-green-600 text-white px-10 py-5 rounded-lg text-2xl font-semibold flex items-center space-x-3 transition-all duration-300"
            style={{
              clipPath: 'polygon(20% 0%, 100% 0%, 100% 100%, 20% 100%, 0% 50%)',
            }}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            <span>이전</span>
          </button>

          {/* Next Button */}
          <button
            onClick={handleNext}
            disabled={!selectedPayment}
            className={`px-10 py-5 rounded-lg text-2xl font-semibold flex items-center space-x-3 transition-all duration-300 ${
              selectedPayment
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-orange-300 text-white cursor-not-allowed'
            }`}
            style={{
              clipPath: 'polygon(0% 0%, 80% 0%, 100% 50%, 80% 100%, 0% 100%)',
            }}
          >
            <span>다음</span>
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          </button>
        </div>

        {/* Bottom Instructions */}
        <p className="text-lg text-gray-600 text-center">터치스크린을 사용하여 입력해주세요</p>
      </div>
    </div>
  );
}
